import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  TextField,
  IconButton,
  Drawer,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const CONDITION_OPTIONS = ["New", "Open Box", "Used", "Parts"];

function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

function fmtUnix(ts) {
  if (!ts) return null;
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return null;
  const ms = n < 10_000_000_000 ? n * 1000 : n;
  return new Date(ms);
}

function formatListed(ts) {
  const d = fmtUnix(ts);
  if (!d) return { short: null, full: null };
  const full = d.toLocaleString();
  const short = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
  return { short, full };
}

function ImageCarousel({ images }) {
  const imgs = Array.isArray(images) ? images.filter(Boolean) : [];
  const [idx, setIdx] = useState(0);

  useEffect(() => setIdx(0), [imgs.length]);

  if (!imgs.length) {
    return (
      <Box
        sx={{
          width: "100%",
          aspectRatio: "4/3",
          bgcolor: "rgba(255,255,255,0.06)",
          borderRadius: 2,
        }}
      />
    );
  }

  const prev = () => setIdx((i) => (i - 1 + imgs.length) % imgs.length);
  const next = () => setIdx((i) => (i + 1) % imgs.length);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "4/3",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "rgba(255,255,255,0.04)",
        "&:hover .navBtn": { opacity: 1 },
      }}
    >
      <Box component="img" src={imgs[idx]} alt="" loading="lazy" sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />

      {imgs.length > 1 && (
        <>
          <IconButton
            onClick={prev}
            size="small"
            className="navBtn"
            sx={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "rgba(0,0,0,0.38)",
              color: "white",
              opacity: 0,
              transition: "opacity 160ms ease, background 160ms ease",
              "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
            }}
          >
            <ArrowBackIosNewIcon fontSize="inherit" />
          </IconButton>

          <IconButton
            onClick={next}
            size="small"
            className="navBtn"
            sx={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "rgba(0,0,0,0.38)",
              color: "white",
              opacity: 0,
              transition: "opacity 160ms ease, background 160ms ease",
              "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
            }}
          >
            <ArrowForwardIosIcon fontSize="inherit" />
          </IconButton>

          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              left: 0,
              right: 0,
              display: "flex",
              gap: 0.75,
              justifyContent: "center",
              pointerEvents: "none",
              opacity: 0.9,
            }}
          >
            {imgs.slice(0, 7).map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: i === idx ? 16 : 6,
                  height: 6,
                  borderRadius: 999,
                  bgcolor: i === idx ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.35)",
                  transition: "all 160ms ease",
                }}
              />
            ))}
            {imgs.length > 7 && (
              <Box sx={{ fontSize: 11, color: "rgba(255,255,255,0.6)", ml: 0.5 }}>+{imgs.length - 7}</Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

function FilterPanel({
  total,
  cats,
  category,
  setCategory,
  condCounts,
  selectedConditions,
  toggleCondition,
  minPrice,
  setMinPrice,
  maxPrice,
  setMaxPrice,
  sort,
  setSort,
  clearFilters,
}) {
  const menuProps = {
    PaperProps: {
      sx: {
        bgcolor: "rgba(18,24,32,0.98)",
        color: "#e7eef8",
        border: "1px solid rgba(255,255,255,0.10)",
        "& .MuiMenuItem-root": { color: "#e7eef8" },
        "& .MuiMenuItem-root.Mui-selected": { bgcolor: "rgba(255,255,255,0.08)" },
      },
    },
  };

  return (
    <Box sx={{ color: "#e7eef8" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontWeight: 800 }}>Filters</Typography>
        <Button onClick={clearFilters} size="small" sx={{ color: "rgba(231,238,248,0.85)", textTransform: "none" }}>
          Clear
        </Button>
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.10)", mb: 2 }} />

      <Typography sx={{ opacity: 0.8, fontSize: 13, mb: 1 }}>
        Results: <b>{total}</b>
      </Typography>

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel sx={{ color: "rgba(231,238,248,0.75)" }}>Category</InputLabel>
        <Select
          value={category}
          label="Category"
          onChange={(e) => setCategory(e.target.value)}
          MenuProps={menuProps}
          sx={{
            bgcolor: "rgba(255,255,255,0.06)",
            color: "#e7eef8",
            "& .MuiSvgIcon-root": { color: "rgba(231,238,248,0.75)" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.14)" },
          }}
        >
          <MenuItem value="">All</MenuItem>
          {cats.map((c) => (
            <MenuItem key={c.name} value={c.name}>
              {c.name} ({c.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.75, opacity: 0.9 }}>Condition</Typography>
        <FormGroup>
          {CONDITION_OPTIONS.map((name) => {
            const count = condCounts[name] ?? null;
            return (
              <FormControlLabel
                key={name}
                control={
                  <Checkbox
                    checked={selectedConditions.includes(name)}
                    onChange={() => toggleCondition(name)}
                    size="small"
                    sx={{
                      color: "rgba(231,238,248,0.55)",
                      "&.Mui-checked": { color: "rgba(231,238,248,0.92)" },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, opacity: 0.92 }}>
                    {name} {count !== null ? <span style={{ opacity: 0.6 }}>({count})</span> : null}
                  </Typography>
                }
                sx={{ my: -0.25 }}
              />
            );
          })}
        </FormGroup>
      </Box>

      <TextField
        value={minPrice}
        onChange={(e) => setMinPrice(e.target.value)}
        label="Min online price"
        size="small"
        fullWidth
        sx={{
          mb: 2,
          "& .MuiInputBase-root": { bgcolor: "rgba(255,255,255,0.06)", color: "#e7eef8", borderRadius: 2 },
          "& fieldset": { borderColor: "rgba(255,255,255,0.14)" },
          "& label": { color: "rgba(231,238,248,0.75)" },
        }}
      />

      <TextField
        value={maxPrice}
        onChange={(e) => setMaxPrice(e.target.value)}
        label="Max online price"
        size="small"
        fullWidth
        sx={{
          mb: 2,
          "& .MuiInputBase-root": { bgcolor: "rgba(255,255,255,0.06)", color: "#e7eef8", borderRadius: 2 },
          "& fieldset": { borderColor: "rgba(255,255,255,0.14)" },
          "& label": { color: "rgba(231,238,248,0.75)" },
        }}
      />

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel sx={{ color: "rgba(231,238,248,0.75)" }}>Sort</InputLabel>
        <Select
          value={sort}
          label="Sort"
          onChange={(e) => setSort(e.target.value)}
          MenuProps={menuProps}
          sx={{
            bgcolor: "rgba(255,255,255,0.06)",
            color: "#e7eef8",
            "& .MuiSvgIcon-root": { color: "rgba(231,238,248,0.75)" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.14)" },
          }}
        >
          <MenuItem value="newest">Newest arrivals</MenuItem>
          <MenuItem value="price_asc">Price: low to high</MenuItem>
          <MenuItem value="price_desc">Price: high to low</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
}

export default function PublicCatalog() {
  const [q, setQ] = useState("");
  const qd = useDebounced(q);

  const [category, setCategory] = useState("");
  const [selectedConditions, setSelectedConditions] = useState([]);

  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const pageSize = 24;

  const [cats, setCats] = useState([]);
  const [condCounts, setCondCounts] = useState({}); // { "New": 123, ... }

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await fetch("/api/public/categories", { signal: ctrl.signal });
        const js = await res.json();
        if (!ctrl.signal.aborted) setCats(js.categories || []);
      } catch (e) {
        if (e?.name === 'AbortError') return;
        setCats([]);
      }
    })();

    (async () => {
      try {
        const res = await fetch("/api/public/conditions", { signal: ctrl.signal });
        const js = await res.json();
        if (ctrl.signal.aborted) return;
        const list = js.conditions || [];
        const map = {};
        for (const c of list) map[c.name] = Number(c.count || 0);
        setCondCounts(map);
      } catch (e) {
        if (e?.name === 'AbortError') return;
        setCondCounts({});
      }
    })();
    return () => ctrl.abort();
  }, []);

  // Memoize the joined-conditions key so the deps array is a stable string.
  const conditionsKey = useMemo(() => selectedConditions.join("|"), [selectedConditions]);

  useEffect(() => setPage(1), [qd, category, minPrice, maxPrice, sort, conditionsKey]);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const url = new URL("/api/public/items", window.location.origin);
        url.searchParams.set("page", String(page));
        url.searchParams.set("page_size", String(pageSize));
        url.searchParams.set("sort", sort);
        if (qd) url.searchParams.set("q", qd);
        if (category) url.searchParams.set("category", category);
        if (minPrice) url.searchParams.set("min_price", minPrice);
        if (maxPrice) url.searchParams.set("max_price", maxPrice);
        selectedConditions.forEach((c) => url.searchParams.append("condition", c));

        const res = await fetch(url.pathname + url.search, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const js = await res.json();
        if (ctrl.signal.aborted) return;

        setItems(js.items || []);
        setTotal(Number(js.total || 0));
      } catch (e) {
        if (e?.name === 'AbortError') return;
        setItems([]);
        setTotal(0);
        setErr("Failed to load items.");
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
    // conditionsKey instead of selectedConditions to avoid array-ref churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, qd, category, minPrice, maxPrice, sort, conditionsKey]);

  const clearFilters = () => {
    setQ("");
    setCategory("");
    setSelectedConditions([]);
    setMinPrice("");
    setMaxPrice("");
    setSort("newest");
    setPage(1);
  };

  const toggleCondition = (name) => {
    setSelectedConditions((prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]));
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#0b0f14", color: "#e7eef8" }}>
      <AppBar position="sticky" sx={{ bgcolor: "rgba(11,15,20,0.88)", backdropFilter: "blur(10px)" }}>
        <Toolbar
          sx={{
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            py: 1,
          }}
        >
          {/* Top row: Brand + Contact */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: "1 1 auto" }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 900, cursor: "pointer", userSelect: "none" }}
              onClick={() => (window.location.href = "/")}
            >
              OnlineRetailerVA
            </Typography>
          </Box>

          <Button
            variant="contained"
            sx={{
              bgcolor: "rgba(255,255,255,0.14)",
              color: "#e7eef8",
              boxShadow: "none",
              "&:hover": { bgcolor: "rgba(255,255,255,0.18)", boxShadow: "none" },
            }}
            onClick={() => (window.location.href = "/contact")}
          >
            Contact
          </Button>

          {/* Second row on mobile: Filters + Search full width */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              flex: "1 1 100%",
            }}
          >
            <IconButton
              onClick={() => setMobileFiltersOpen(true)}
              sx={{
                display: { xs: "inline-flex", md: "none" },
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#e7eef8",
              }}
              aria-label="Open filters"
            >
              <FilterAltIcon />
            </IconButton>

            <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <SearchIcon sx={{ opacity: 0.8, flex: "0 0 auto" }} />
              <TextField
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search items..."
                size="small"
                fullWidth
                sx={{
                  "& .MuiInputBase-root": {
                    bgcolor: "rgba(255,255,255,0.06)",
                    color: "#e7eef8",
                    borderRadius: 2,
                  },
                  "& fieldset": { borderColor: "rgba(255,255,255,0.14)" },
                }}
              />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "280px 1fr" }, gap: 2, p: 2 }}>
        {/* Desktop filters */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              position: "relative",
              width: 280,
              bgcolor: "rgba(255,255,255,0.045)",
              color: "#e7eef8",
              borderColor: "rgba(255,255,255,0.10)",
              borderRadius: 2,
              p: 2,
            },
          }}
          open
        >
          <FilterPanel
            total={total}
            cats={cats}
            category={category}
            setCategory={setCategory}
            condCounts={condCounts}
            selectedConditions={selectedConditions}
            toggleCondition={toggleCondition}
            minPrice={minPrice}
            setMinPrice={setMinPrice}
            maxPrice={maxPrice}
            setMaxPrice={setMaxPrice}
            sort={sort}
            setSort={setSort}
            clearFilters={clearFilters}
          />
        </Drawer>

        {/* Mobile filters drawer */}
        <Drawer
          anchor="left"
          open={mobileFiltersOpen}
          onClose={() => setMobileFiltersOpen(false)}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: 300,
              p: 2,
              bgcolor: "rgba(14,18,24,0.98)",
              color: "#e7eef8",
              borderRight: "1px solid rgba(255,255,255,0.10)",
            },
          }}
        >
          <FilterPanel
            total={total}
            cats={cats}
            category={category}
            setCategory={(v) => {
              setCategory(v);
              setMobileFiltersOpen(false);
            }}
            condCounts={condCounts}
            selectedConditions={selectedConditions}
            toggleCondition={toggleCondition}
            minPrice={minPrice}
            setMinPrice={setMinPrice}
            maxPrice={maxPrice}
            setMaxPrice={setMaxPrice}
            sort={sort}
            setSort={setSort}
            clearFilters={clearFilters}
          />
        </Drawer>

        {/* Results */}
        <Box>
          {err && (
            <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: "rgba(255,0,0,0.08)", border: "1px solid rgba(255,255,255,0.10)" }}>
              {err}
            </Box>
          )}

          {!loading && !err && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: "rgba(255,255,255,0.035)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <Typography sx={{ fontSize: 13, opacity: 0.85 }}>
                Everything shown is available. Local pickup by appointment only — text to arrange a pickup.
              </Typography>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", lg: "repeat(3,1fr)", xl: "repeat(4,1fr)" },
                gap: 2,
              }}
            >
              {items.map((it) => {
                const listed = formatListed(it.listed_ts);

                return (
                  <Box
                    key={it.id}
                    sx={{
                      p: 1.6,
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.045)",
                      border: "1px solid rgba(255,255,255,0.09)",
                      boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
                      transition: "transform 160ms ease, border-color 160ms ease, background 160ms ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        borderColor: "rgba(255,255,255,0.16)",
                        bgcolor: "rgba(255,255,255,0.055)",
                      },
                    }}
                  >
                    <ImageCarousel images={it.images} />

                    <Box sx={{ mt: 1.1 }}>
                      <Typography
                        sx={{
                          fontWeight: 850,
                          fontSize: 15,
                          lineHeight: 1.25,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {it.title}
                      </Typography>

                      <Box sx={{ mt: 0.6, display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: 13, opacity: 0.9 }}>{it.condition}</Typography>
                        <Typography sx={{ opacity: 0.35 }}>•</Typography>
                        <Typography sx={{ fontSize: 13, opacity: 0.85 }}>{it.category}</Typography>

                        {listed.short ? (
                          <>
                            <Typography sx={{ opacity: 0.35 }}>•</Typography>
                            <Tooltip title={listed.full || ""} arrow>
                              <Typography sx={{ fontSize: 12.5, opacity: 0.78 }}>Listed {listed.short}</Typography>
                            </Tooltip>
                          </>
                        ) : null}
                      </Box>

                      {/* Price row */}
                      <Box
                        sx={{
                          mt: 1.2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          p: 1.1,
                          borderRadius: 2,
                          bgcolor: "rgba(255,255,255,0.06)",
                          border: "1px solid rgba(255,255,255,0.10)",
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 11.5, opacity: 0.7, letterSpacing: 0.3 }}>ONLINE</Typography>
                          <Typography sx={{ fontWeight: 900, fontSize: 18, lineHeight: 1.1 }}>
                            {money(it.online_price)}
                          </Typography>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.10)" }} />

                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 11.5, opacity: 0.7, letterSpacing: 0.3 }}>LOCAL</Typography>
                          <Typography sx={{ fontWeight: 850, fontSize: 16, lineHeight: 1.1, opacity: 0.92 }}>
                            {money(it.local_price)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 3 }}>
            <Button
              variant="outlined"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              sx={{ borderColor: "rgba(255,255,255,0.18)", color: "#e7eef8" }}
            >
              Prev
            </Button>

            <Chip label={`Page ${page} / ${totalPages}`} sx={{ bgcolor: "rgba(255,255,255,0.06)", color: "rgba(231,238,248,0.85)" }} />

            <Button
              variant="outlined"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              sx={{ borderColor: "rgba(255,255,255,0.18)", color: "#e7eef8" }}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}